import React from 'react';

interface PasswordStrengthIndicatorProps {
  password: string;
}

interface PasswordRequirement {
  label: string;
  test: (password: string) => boolean;
}

const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({ password }) => {
  const requirements: PasswordRequirement[] = [
    {
      label: 'حداقل 8 کاراکتر',
      test: (pwd) => pwd.length >= 8
    },
    {
      label: 'شامل حرف بزرگ',
      test: (pwd) => /[A-Z]/.test(pwd)
    },
    {
      label: 'شامل حرف کوچک',
      test: (pwd) => /[a-z]/.test(pwd)
    },
    {
      label: 'شامل عدد',
      test: (pwd) => /\d/.test(pwd)
    },
    {
      label: 'شامل کاراکتر خاص',
      test: (pwd) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd)
    }
  ];

  const metRequirements = requirements.filter(req => req.test(password));
  const strength = metRequirements.length;
  const strengthPercentage = (strength / requirements.length) * 100;

  const getStrengthColor = () => {
    if (strength <= 2) return 'bg-red-500';
    if (strength <= 3) return 'bg-yellow-500';
    if (strength <= 4) return 'bg-blue-500';
    return 'bg-green-500';
  };

  const getStrengthText = () => {
    if (strength <= 2) return 'ضعیف';
    if (strength <= 3) return 'متوسط';
    if (strength <= 4) return 'خوب';
    return 'قوی';
  };

  const getStrengthTextColor = () => {
    if (strength <= 2) return 'text-red-600';
    if (strength <= 3) return 'text-yellow-600';
    if (strength <= 4) return 'text-blue-600';
    return 'text-green-600';
  };

  if (!password) return null;

  return (
    <div className="mt-2 space-y-2" dir="rtl">
      {/* Strength Bar */}
      <div className="flex items-center space-x-reverse space-x-2">
        <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
          <div 
            className={`h-full transition-all duration-500 ease-out ${getStrengthColor()}`}
            style={{ width: `${strengthPercentage}%` }}
          />
        </div>
        <span className={`text-sm font-medium transition-colors duration-300 ${getStrengthTextColor()}`}>
          {getStrengthText()}
        </span>
      </div>

      {/* Requirements List */}
      <div className="space-y-1">
        {requirements.map((requirement, index) => {
          const isMet = requirement.test(password);
          return (
            <div 
              key={index}
              className={`flex items-center space-x-reverse space-x-2 text-sm transition-all duration-300 ${
                isMet ? 'text-green-600' : 'text-gray-500'
              }`}
            >
              <div className={`w-4 h-4 rounded-full flex items-center justify-center transition-all duration-300 ${
                isMet ? 'bg-green-500 scale-110' : 'bg-gray-300'
              }`}>
                {isMet && (
                  <svg 
                    className="w-3 h-3 text-white animate-pulse" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M5 13l4 4L19 7" 
                    />
                  </svg>
                )}
              </div>
              <span className={`transition-all duration-300 ${
                isMet ? 'font-medium' : 'font-normal'
              }`}>
                {requirement.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PasswordStrengthIndicator; 